import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useAgentComponentToolOptions } from '../useAgentComponents';
import { AddToolDialog } from './AddToolDialog';
import { AttachedToolItem } from './AttachedToolItem';
import { TOOLS_FIELD_COPY } from './component-page-copy';
import styles from './ToolsField.module.css';

interface ToolsFieldProps {
  toolDefinitionIds: string[];
  onChange: (toolDefinitionIds: string[]) => void;
}

/** The partner tools a component can call, with a picker for adding more of its active tools. */
export const ToolsField = ({ toolDefinitionIds, onChange }: ToolsFieldProps) => {
  const [addingTool, setAddingTool] = useState(false);
  const { data: activeTools = [], isLoading } = useAgentComponentToolOptions();
  const activeToolsById = new Map(activeTools.map(tool => [tool.id, tool]));
  const availableTools = activeTools.filter(tool => !toolDefinitionIds.includes(tool.id));
  const componentHasTools = toolDefinitionIds.length > 0;
  const toolsAreLoaded = !isLoading;
  const showNoToolsNote = toolsAreLoaded && !componentHasTools;
  const showAttachedTools = toolsAreLoaded && componentHasTools;

  const closeToolPicker = () => setAddingTool(false);
  const addTools = (toolIds: string[]) => {
    onChange([...toolDefinitionIds, ...toolIds]);
    closeToolPicker();
  };
  const removeTool = (toolId: string) =>
    onChange(toolDefinitionIds.filter(attachedToolId => attachedToolId !== toolId));

  return (
    <fieldset className={styles.tools}>
      <legend>{TOOLS_FIELD_COPY.legend}</legend>
      <p className={styles.help}>{TOOLS_FIELD_COPY.help}</p>
      <button
        type="button"
        className={styles.addTool}
        disabled={isLoading}
        onClick={() => setAddingTool(true)}>
        <Plus aria-hidden="true" />
        {TOOLS_FIELD_COPY.addTool}
      </button>
      {addingTool && <AddToolDialog tools={availableTools} onAdd={addTools} onClose={closeToolPicker} />}
      {isLoading && <p className={styles.note}>{TOOLS_FIELD_COPY.loading}</p>}
      {showNoToolsNote && <p className={styles.note}>{TOOLS_FIELD_COPY.noTools}</p>}
      {showAttachedTools && (
        <ul>
          {toolDefinitionIds.map(toolId => (
            <AttachedToolItem
              key={toolId}
              tool={activeToolsById.get(toolId)}
              onRemove={() => removeTool(toolId)}
            />
          ))}
        </ul>
      )}
    </fieldset>
  );
};
